import { addDisplayVersion, applyDisplayVersion, canLeadScenarios, removeDisplayVersion, versionRollKey, writeDisplayVersion, type DisplayVersion } from '@agavi/blocks';
import { Dices } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EditorSectionHeading, PhoneModeToggle } from './input-block-frame';
import { ScenarioStimulus } from './scenario-stimulus';
import { useBlockVersions } from './use-block-versions';
import { useIsPhone } from './use-viewport';
import { VersionSelector } from './version-selector';

import type { BlockUpdateOptions } from '@/lib/block-update';
import type { Block } from '@/types/experience';

import { IconButton } from '@/components/ui/icon-button';
import { TextField } from '@/components/ui/text-field';
import { followersLosingAnswers, scenarioFollowersOf } from '@/lib/scenario-links';

/**
 * Everything a DISPLAY block needs to hold a bank of variants and lead a scenario group.
 *
 * Text had all of this written out by hand and Image, Sound and DataViz had none of it. Copying
 * it three more times is the failure `use-block-versions` already records for the input blocks:
 * the subtle parts are subtle in the same way every time, and the copies drift.
 *
 * Four things are subtle enough to have been got wrong once already:
 *
 *   - **Three answers to "which variant", not two.** Editing shows the one being authored, a
 *     STUDENT shows the one the task drew, and the CARD shows the test pointer — because the
 *     card and the editor are two instances of the same component on screen at once, and one
 *     pointer makes stepping the test move the author's text under them.
 *   - **A write must send the WHOLE data object.** `onUpdate` replaces `block.data` wholesale,
 *     so a write naming only the field that changed deletes the bank. That is the same
 *     delete-by-omission that cost a question bank once already.
 *   - **A variant is born NAMED.** A follower's row names it and so does this block's own pager,
 *     and those have to agree — which ghost text cannot do.
 *   - **The closed card's pager needs a real bank, not just followers.** A block whose variants
 *     were deleted back to one still has followers pointing at it.
 */
export function useDisplayVersions(
  block: Block,
  /** The fields that VARY per variant. Everything else stays on the block and is shared — the
   *  same split `question-versions.md` §3.2 makes: content varies, presentation does not. */
  keys: readonly string[],
  onUpdate: (data: Record<string, unknown>, opts?: BlockUpdateOptions) => void,
  ctx: {
    isPreview: boolean;
    isEditing: boolean;
    /** The task's draw, which is what a STUDENT sees. */
    taskPreviewRoll: Record<string, string> | null | undefined;
    /** Every block in the task, for "does anything follow me". */
    blocks: Block[] | undefined;
    /** Names variant N — passed in because it is a translated string. */
    nameFor: (index: number) => string;
    /** §7.3's arm hint, built here because it is a translated string with a list in it. */
    warnFor: (names: string[]) => string;
    /**
     * The block's NORMALIZED read model — its own `toData(block.data)`.
     *
     * Reads project onto this; writes still merge onto the raw stored object, and the two are
     * not interchangeable. A brand-new block persists `data: {}`, so projecting onto the raw
     * object hands back an object with no keys at all — which crashed DataViz the moment it
     * reached `data.series.some(...)`, because `series` is required and a default only exists
     * inside `toData`. Writes cannot use the normalized copy for the opposite reason: it is
     * built field by field and would drop anything it does not name.
     */
    base?: Record<string, unknown>;
  },
) {
  const { shownVersionId, setTestVersion, editorVersionId, setEditorVersion } = useBlockVersions(block);
  const data = block.data as Record<string, unknown> & { versions?: DisplayVersion[] };
  /* Read from the data rather than from `useBlockVersions`, which types its list as `{id}[]`
     for the input blocks. A display variant carries a NAME, and the pager prints it. */
  const bank = data.versions ?? [];

  const activeVersionId = ctx.isPreview
    ? ctx.taskPreviewRoll?.[versionRollKey(block.id)] ?? shownVersionId
    : ctx.isEditing ? editorVersionId : shownVersionId;

  /* Projected onto the NORMALIZED base, so a block that has never been saved still has its
     defaults. `versions` is carried across because the base does not name it. */
  const readBase = (ctx.base ? { ...ctx.base, versions: data.versions } : data) as typeof data;
  /** The block's content with the variant in force projected onto it. Identity with no bank. */
  const shown = applyDisplayVersion(readBase, activeVersionId, keys) as typeof data;
  /** The TEST pointer's variant, which the card and the phone test pane read even while editing. */
  const testShown = applyDisplayVersion(readBase, shownVersionId, keys) as typeof data;

  const hasBank = bank.length > 1;

  /**
   * Write edited fields back, into the version being edited when there is one.
   *
   * Takes a PATCH and merges it onto the whole stored object, so a caller cannot delete the
   * bank by naming only what changed.
   */
  const writeFields = (patch: Record<string, unknown>, opts?: BlockUpdateOptions) => {
    onUpdate(hasBank
      ? writeDisplayVersion(data, editorVersionId, { ...data, ...patch }, keys)
      : { ...data, ...patch }, opts);
  };

  function addVariant() {
    const { data: next, addedId } = addDisplayVersion(
      data, editorVersionId, () => crypto.randomUUID(), keys, ctx.nameFor,
    );
    onUpdate(next);
    // Point the editor at what was just made: "+" means author a DIFFERENT variant, not look
    // again at the one it was copied from.
    setEditorVersion(addedId);
  }

  function removeVariant(id: string) {
    const next = removeDisplayVersion(data, id, keys);
    // Says WHICH variant went, because `next` cannot: dropping to one variant collapses the
    // bank, so the write that follows shows no versions at all and the followers' prune would
    // have no way to tell the survivor from the one just deleted.
    onUpdate(next, { removedVersionId: id });
    setEditorVersion((next.versions ?? [])[0]?.id);
  }

  function renameVariant(name: string) {
    onUpdate({
      ...data,
      versions: (data.versions ?? []).map(v => (v.id === editorVersionId ? { ...v, name: name || undefined } : v)),
    });
  }

  /**
   * Does the closed card keep its pager? Only for a block that is actually leading a group.
   *
   * Both halves are load-bearing. Followers alone let a block whose variants were deleted back
   * down to one keep a bar holding nothing but a Reroll dice — a scenario controller on a block
   * with no scenarios. A bank alone would sprout a bar on every versioned display block on the
   * canvas, when a bank nothing follows affects only the block you are already looking at.
   *
   * `canLeadScenarios` is the same two-variant line the link menu draws, so what may be paged
   * from a closed card and what may be followed cannot come apart.
   */
  const leadsGroup = canLeadScenarios(data) && scenarioFollowersOf(block.id, ctx.blocks).length > 0;

  /*
   * §7.3 — WHAT ELSE GOES IF THIS VARIANT GOES.
   *
   * Deleting a variant removes the matching row from every follower, and `followersLosingAnswers`
   * narrows that to the ones who would lose an answer somebody WROTE. Undefined while nothing is
   * at stake, which is most of the time: during authoring every row is still an untouched copy.
   *
   * Computed for the variant the EDITOR is on, because that is the one the bank row's delete
   * removes. Read before arming, so the hint's space can be reserved without the mount/unmount
   * that silently disarms the button — see `feedback_arm_hint_layout_shift`.
   */
  const losing = editorVersionId ? followersLosingAnswers(block.id, editorVersionId, ctx.blocks) : [];
  const removeWarning = losing.length > 0
    ? ctx.warnFor(losing.map(b => b.name ?? b.id))
    : undefined;

  return {
    bank, hasBank, leadsGroup, removeWarning,
    shown, testShown,
    shownVersionId, setTestVersion,
    editorVersionId, setEditorVersion,
    writeFields, addVariant, renameVariant,
    removeVariant,
  };
}


/**
 * THE BANK ROW — the display blocks' answer to the input blocks' question heading.
 *
 * `EditorSectionHeading` is the one every input block puts its pager in, and it brings the rule
 * and the spacing with it; a hand-rolled row here was a copy of its label classes plus guesses
 * at the padding, which is the fork the house rule exists to stop.
 *
 * THE LABEL IS LOAD-BEARING. A bare `+` reads as a stray control; a labelled section reads as
 * furniture. "Content" is the display blocks' answer to the input blocks' QUESTION.
 *
 * Once a bank exists the label becomes the NAMER — the same move the Decimals/Sig figs switch
 * makes, where the label was already saying what the control is about. A variant always HAS a
 * name to show, so the swap never leaves the slot empty.
 */
export function DisplayBankRow({ bank, editorVersionId, setEditorVersion, addVariant, removeVariant, renameVariant, label, removeWarning, divider = false }: {
  bank: DisplayVersion[];
  /**
   * The heading this row IS, when the block already has one to host the pager.
   *
   * Text, Image and Sound have no heading above their content, so the row brings one —
   * "Content", the display blocks' answer to the input blocks' "Question". DataViz already
   * opens with "Graph Setup", and a second heading directly above it would be furniture
   * announcing furniture. It passes its own, and the row becomes that heading rather than a
   * sibling of it.
   */
  label?: string;
  editorVersionId: string | undefined;
  setEditorVersion: (id: string | undefined) => void;
  addVariant: () => void;
  removeVariant: (id: string) => void;
  renameVariant: (name: string) => void;
  /**
   * §7.3 — what else this variant's delete takes with it, or undefined when nothing is at stake.
   *
   * A DELETE IS STILL TWO CLICKS AND NOTHING POPS UP. Removing a variant is more destructive
   * than the rest of the bank row because it reaches into other blocks, so the arm says what it
   * will cost instead of a dialog interrupting a gesture that already asks twice.
   */
  removeWarning?: string;
  /** Text passes false: its styling bar already draws a rule, and two would box the heading in. */
  divider?: boolean;
}) {
  const { t } = useTranslation();
  const active = bank.find(v => v.id === editorVersionId);
  const [armed, setArmed] = useState(false);
  return (
    <>
    <EditorSectionHeading
      label={bank.length > 1 ? (
        <TextField
          variant='prose'
          value={active?.name ?? ''}
          onChange={renameVariant}
          ariaLabel={t('blocks.versions.nameLabel')}
          /* CAPPED. Left to grow it pushes the pager's delete off the panel's right edge — the
             heading row is `justify-between`, so a label with no ceiling takes the room the
             action slot needs. */
          className='w-full max-w-[13rem]'
        />
      ) : (label ?? t('blocks.areas.content'))}
      action={(
        <VersionSelector
          versions={bank}
          activeId={editorVersionId}
          onSelect={setEditorVersion}
          onAdd={addVariant}
          onRemove={removeVariant}
          onArmedChange={setArmed}
          noun='scenario'
        />
      )}
      divider={divider}
    />
    {/*
      * MOUNTED ON ARM, pushing what is below it down. Reserving the space instead left a
      * permanent gap between the heading and the content for a warning almost nobody will ever
      * see — the owner's call, and the right trade for how rarely this fires.
      *
      * `feedback_arm_hint_layout_shift` is why that is not the obvious choice: mounting next to
      * an armed button can change a scrollable panel's height, the browser scrolls to keep the
      * focused button in view, and the button slides out from under a stationary cursor, whose
      * `pointerleave` disarms it before the second click lands. The disarm is guarded against
      * that in `VersionSelector` — see `ARM_SETTLE_MS` — so the mount is safe here.
      */}
    {removeWarning && armed && (
      <p className='px-1 pb-1 text-xs text-ws-warning-fg'>{removeWarning}</p>
    )}
    </>
  );
}

/**
 * THE CARD'S TEST BAR — stepping a variant, and rerolling the variables with it.
 *
 * Browse-mode `VersionSelector` (no add, no remove), which renders nothing at all below two
 * variants — so a block with variables and one variant gets Reroll alone rather than a pager
 * reading "1 of 1".
 *
 * Both controls stop their own click, because this bar rides on a card whose whole surface
 * opens the block and the one thing a teacher paging it wants is to not open it.
 */
export function DisplayScenarioBar({ bank, activeId, onSelect, onReroll }: {
  bank: DisplayVersion[];
  activeId: string | undefined;
  onSelect: (id: string | undefined) => void;
  /**
   * Roll the VARIABLES. Omitted when there is nothing to roll, and the dice disappears with it.
   *
   * It used to redraw the variant as well, on the argument that rerolling is the realistic pass
   * and stepping is the deliberate one. The owner reversed that, and the reason is that the two
   * acts already have one control each: the pager steps scenarios, the dice rolls numbers. A
   * dice that also redrew the scenario left no way to say "same scenario, different numbers",
   * which is exactly what checking an answer key across rolls needs. **Only full Preview draws a
   * scenario at random** — that is the one surface standing in for a student.
   */
  onReroll?: () => void;
}) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  return (
    /* `mb-3`, not `mt-3`: this sits ABOVE the content it pages. Below it, its position moved
       with the length of whatever variant was showing — a two-line paragraph and a
       ten-line one put the pager in different places, so stepping through scenarios on a
       phone meant chasing the control down the card between every click. */
    /*
     * TIGHTER ON A PHONE, and decided by the HOOK rather than a Tailwind breakpoint.
     *
     * Phone LANDSCAPE is 667px wide — above Tailwind's `sm` (640px) — so `sm:` cannot tell it
     * from a small desktop and a width-based rule silently did nothing there, which is the one
     * surface that needed it most. `PHONE_QUERY` is `max-width: 767px` OR a short coarse-pointer
     * viewport, so it catches both orientations.
     *
     * The control inside is 28px and that is a touch target, so the chrome is the only slack:
     * at `py-1.5 mb-3` the bar was 54px of a 162px card in landscape, a third of the card spent
     * on a pager.
     */
    <div className={`flex items-center justify-center gap-2 rounded-md border border-ws-border bg-ws-hover px-3 ${isPhone ? 'mb-2 py-0.5' : 'mb-3 py-1.5'}`}>
      {onReroll && (
        <IconButton
          type='button'
          size='sm'
          aria-label={t('blocks.testBar.reroll')}
          onClick={e => { e.stopPropagation(); onReroll(); }}
        >
          <Dices className='h-4 w-4' />
        </IconButton>
      )}
      <VersionSelector versions={bank} activeId={activeId} onSelect={onSelect} noun='scenario' showNoun />
    </div>
  );
}

/**
 * THE PHONE'S EDIT/TEST TABS AND ITS TEST PANE, for the display blocks that are not Text.
 *
 * Text grew these first and kept its own, because its test pane re-renders authored prose with
 * the variables resolved and nothing else can borrow that. Image, Sound and DataViz only ever
 * need "show me the drawn variant, large, read-only" — which `ScenarioStimulus` already does,
 * including the asset loading, because a follower's ghosted stem needs exactly the same thing.
 *
 * The owner asked for these for CONSISTENCY rather than because image and sound are useful to
 * test: *"probably for image/sound they are not useful, but in dataviz they may be if we have
 * variables in the data."* The surface existing on one display block and not the others is the
 * kind of gap that sends a teacher looking for a control that is not there — and a phone is
 * the one place the leader is never on screen beside its followers.
 *
 * Returns `null` when there is nothing to test, so the tabs never offer an empty screen — the
 * same condition `InputBlockFrame` and Text both apply before showing their own.
 */
export function useDisplayPhoneTest({ block, bank, shownVersionId, setTestVersion, phoneMode, setPhoneMode, onReroll, enabled }: {
  block: Block;
  bank: DisplayVersion[];
  shownVersionId: string | undefined;
  setTestVersion: (id: string | undefined) => void;
  phoneMode: 'edit' | 'test';
  setPhoneMode: (m: 'edit' | 'test') => void;
  onReroll?: () => void;
  /**
   * Phone only. Taken as a FLAG rather than letting the caller skip the call, because this runs
   * `useTranslation` — calling it conditionally changes hook order the moment the viewport
   * crosses the phone breakpoint, which is a real crash and not only a lint complaint. Every
   * hook above the early return; the return decides whether anything renders.
   */
  enabled: boolean;
}) {
  const { t } = useTranslation();
  if (!enabled) return null;
  if (bank.length < 2 && !onReroll) return null;
  const version = bank.find(v => v.id === shownVersionId) ?? bank[0];
  return {
    tabs: (
      <div className='border-b border-ws-border bg-ws-surface px-4 py-2'>
        <PhoneModeToggle
          phoneMode={phoneMode}
          onPhoneModeChange={setPhoneMode}
          editModeLabel={t('blocks.phoneTabs.edit')}
          testModeLabel={t('blocks.phoneTabs.test')}
          /* Looking, not drawing — the same rule Text states: a tab switch that rerolled meant
             you could not flip back and forth to compare one variant against its setup. */
          onSwitchToTest={() => {}}
        />
      </div>
    ),
    pane: phoneMode === 'test' ? (
      <div className='flex flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4 pt-3'>
        {/* The bar above what it pages, as everywhere else. */}
        {bank.length > 1 && (
          <DisplayScenarioBar bank={bank} activeId={shownVersionId} onSelect={setTestVersion} onReroll={onReroll} />
        )}
        <ScenarioStimulus leader={block} version={(version ?? null) as Record<string, unknown> | null} />
      </div>
    ) : null,
  };
}
